<template>
  <div class="gs-app gs-resources-assets gs-asset-wizard custom-scrollbars overflow-y-auto">
    <v-wait for="assets">
      <div v-if="funnelResource" class="gs-asset-wizard__shell">
        <header class="gs-asset-wizard__bar">
          <button
            type="button"
            class="gs-asset-wizard__back"
            @click="sendToFunnelPromoAreas()"
          >
            <gs-icon name="arrow-left" size="16"></gs-icon>
            <span>Promo tools</span>
          </button>
          <div class="gs-asset-wizard__title">
            <span class="gs-asset-wizard__eyebrow">Promo tools · {{ funnelResource && funnelResource.name }}</span>
          </div>
          <div class="gs-asset-wizard__actions">
            <span
              class="gs-asset-wizard__status"
              :class="{ 'is-dirty': isDirty }"
              aria-live="polite"
            >
              <i aria-hidden="true"></i>
              {{ isDirty ? 'Unsaved changes' : 'All changes saved' }}
            </span>
            <gs-button variant="outline" :disabled="!isDirty" @click="save()">Save</gs-button>
            <gs-button variant="primary" @click="saveAssets()">{{ $t('frontend_save_exit') }}</gs-button>
          </div>
        </header>

        <div class="gs-asset-wizard__layout">
          <asset-sidebar
            :items="assetTypeSummaries"
            :current="assetsTab"
            @select="handleAssetsTabChange"
          />

          <main class="gs-asset-wizard__content">
            <div v-if="assetsTab === 'portalSettings'" class="gs-asset-wizard__settings-heading">
              <h1>Portal Settings</h1>
              <p>Choose which landing pages appear, who can see them, and how your affiliate portal looks. Save here, then choose Swipes or another asset type to edit your content.</p>
            </div>
            <promo-area-settings-drawer v-show="assetsTab === 'portalSettings'" ref="portalSettings" inline :model="funnelResource" :resource="funnelResource" :funnel="funnel" :funnels="settingsFunnels" :resources="settingsResources" />
            <asset-section-header
              v-if="assetsTab !== 'portalSettings'"
              :index="currentTypeIndex + 1"
              :total="assetTypeSummaries.length"
              :title="currentTypeLabel"
              :guidance="currentTypeGuidance"
              :enabled="!!funnelResource[currentType.enabledKey]"
              @toggle="toggleSwitchValue(currentType.toggleValue)"
            />

            <div v-if="assetsTab !== 'portalSettings'" class="gs-asset-wizard__panel">
              <div v-if="assetsTab === 'swipes'">
                <swipes-tab
                  :funnel-resource="funnelResource"
                  :swipes-tab="swipesTab"
                  :upload-url="uploadUrl"
                  :landing-pages="landingPages"
                  @handleSwipesTabChange="handleSwipesTabChange"
                  @updateFunnelResource="updateFunnelResource"
                />
              </div>
              <div v-else-if="assetsTab === 'banners'">
                <banners-tab
                  :funnel-resource="funnelResource"
                  :landing-pages="landingPages"
                  @updateFunnelResource="updateFunnelResource"
                />
              </div>
              <div v-else-if="assetsTab === 'thankyouPageAds'">
                <thankyou-page-ads-tab
                  :funnel-resource="funnelResource"
                  :thankyou-page-ads-tab="thankyouPageAdsTab"
                  :upload-url="uploadUrl"
                  :landing-pages="landingPages"
                  @handleThankyouPageAdsTabChange="handleThankyouPageAdsTabChange"
                  @updateFunnelResource="updateFunnelResource"
                />
              </div>
              <div v-else-if="assetsTab === 'signatures'">
                <signatures-tab
                  :funnel-resource="funnelResource"
                  :landing-pages="landingPages"
                  :email-signatures-tab="emailSignaturesTab"
                  :forum-signatures-tab="forumSignaturesTab"
                  @handleEmailSignaturesTabChange="handleEmailSignaturesTabChange"
                  @handleForumSignaturesTabChange="handleForumSignaturesTabChange"
                  @updateFunnelResource="updateFunnelResource"
                />
              </div>
              <div v-else-if="assetsTab === 'socialMediaPosts'">
                <social-media-posts-tab
                  :funnel-resource="funnelResource"
                  :landing-pages="landingPages"
                  :facebook-posts-tab="facebookPostsTab"
                  :twitter-tweets-tab="twitterTweetsTab"
                  :linkedin-posts-tab="linkedinPostsTab"
                  @handleFacebookPostsTabChange="handleFacebookPostsTabChange"
                  @handleTwitterTweetsTabChange="handleTwitterTweetsTabChange"
                  @handleLinkedinPostsTabChange="handleLinkedinPostsTabChange"
                  @updateFunnelResource="updateFunnelResource"
                />
              </div>
              <div v-else-if="assetsTab === 'blogReviews'">
                <blog-reviews-tab
                  :funnel-resource="funnelResource"
                  :landing-pages="landingPages"
                  :blog-reviews-tab="blogReviewsTab"
                  :upload-url="uploadUrl"
                  @handleBlogReviewsTabChange="handleBlogReviewsTabChange"
                  @updateFunnelResource="updateFunnelResource"
                />
              </div>
              <div v-else-if="assetsTab === 'videoReviews'">
                <video-reviews-tab
                  :funnel-resource="funnelResource"
                  :landing-pages="landingPages"
                  :video-reviews-tab="videoReviewsTab"
                  @handleVideoReviewsTabChange="handleVideoReviewsTabChange"
                  @updateFunnelResource="updateFunnelResource"
                />
              </div>
              <div v-else-if="assetsTab === 'facebookAds'">
                <facebook-ads-tab
                  :funnel-resource="funnelResource"
                  :landing-pages="landingPages"
                  :facebook-ads-tab="facebookAdsTab"
                  @handleFacebookAdsTabChange="handleFacebookAdsTabChange"
                  @updateFunnelResource="updateFunnelResource"
                />
              </div>
              <div v-else-if="assetsTab === 'logoutPageAds'">
                <logout-page-ads-tab
                  :funnel-resource="funnelResource"
                  :landing-pages="landingPages"
                  :logout-page-ads-tab="logoutPageAdsTab"
                  :upload-url="uploadUrl"
                  @handleLogoutPageAdsTabChange="handleLogoutPageAdsTabChange"
                  @updateFunnelResource="updateFunnelResource"
                />
              </div>
              <div v-else-if="assetsTab === 'allArticles'">
                <all-articles-tab
                  :funnel-resource="funnelResource"
                  :landing-pages="landingPages"
                  :press-releases-tab="pressReleasesTab"
                  :articles-tab="articlesTab"
                  @handlePressReleasesTabChange="handlePressReleasesTabChange"
                  @handleArticlesTabChange="handleArticlesTabChange"
                  @updateFunnelResource="updateFunnelResource"
                />
              </div>
            </div>
          </main>
        </div>
      </div>
    </v-wait>

    <gs-toast-provider></gs-toast-provider>

    <portal to="headerBreadCrumb">
      <div class="flex items-center cursor-pointer -ml-4" @click="sendToFunnelPromoAreas">
        <chevron-left-icon class="h-3 fill-current" />
      </div>
    </portal>
  </div>
</template>

<script>
import Funnel from '@/models/Funnel';
import FunnelResource from '@/models/FunnelResource';
import { required, minLength, maxLength, url } from 'vuelidate/lib/validators';
import Auth from '@groovepages/gd-lib/auth';

import urlParser from 'js-video-url-parser/lib/base';
import 'js-video-url-parser/lib/provider/youtube';
import 'js-video-url-parser/lib/provider/vimeo';
import SwipesTab from './assets_partials/SwipesTab.vue';
import BannersTab from './assets_partials/BannersTab.vue';
import ThankyouPageAdsTab from './assets_partials/ThankyouPageAdsTab.vue';
import SignaturesTab from './assets_partials/SignaturesTab.vue';
import SocialMediaPostsTab from './assets_partials/SocialMediaPostsTab.vue';
import BlogReviewsTab from './assets_partials/BlogReviewsTab.vue';
import VideoReviewsTab from './assets_partials/VideoReviewsTab.vue';
import FacebookAdsTab from './assets_partials/FacebookAdsTab.vue';
import LogoutPageAdsTab from './assets_partials/LogoutPageAdsTab.vue';
import AllArticlesTab from './assets_partials/AllArticlesTab.vue';
import AssetSidebar from './assets_partials/AssetSidebar.vue';
import PromoAreaSettingsDrawer from './partials/PromoAreaSettingsDrawer.vue';
import AssetSectionHeader from './assets_partials/AssetSectionHeader.vue';
import { ASSET_TYPES, summariseAssetTypes } from './assetTypes';
import ChevronLeftIcon from '@/assets/images/icons/ChevronLeftIcon.svg';

export default {
  layout: 'defaultWithoutSidebar',
  middleware: 'auth',
  metaInfo() {
    return { title: 'Promo Area Assets' };
  },
  components: {
    SwipesTab,
    BannersTab,
    ThankyouPageAdsTab,
    SignaturesTab,
    SocialMediaPostsTab,
    BlogReviewsTab,
    VideoReviewsTab,
    FacebookAdsTab,
    LogoutPageAdsTab,
    AllArticlesTab,
    AssetSidebar,
    PromoAreaSettingsDrawer,
    AssetSectionHeader,
    ChevronLeftIcon,
  },
  data() {
    return {
      funnel: null,
      funnelResource: null,
      // Set by the deep watcher below and cleared by `snapshot()`. The old
      // computed stringified the whole promo area on every re-render of the
      // status line - i.e. on every keystroke - to answer the same question.
      isDirty: false,
      assetsTab: 'portalSettings',
      settingsFunnels: [],
      settingsResources: [],
      swipesTab: '',
      facebookPostsTab: '',
      twitterTweetsTab: '',
      linkedinPostsTab: '',
      emailSignaturesTab: '',
      forumSignaturesTab: '',
      pressReleasesTab: '',
      articlesTab: '',
      blogReviewsTab: '',
      thankyouPageAdsTab: '',
      logoutPageAdsTab: '',
      facebookAdsTab: '',
      videoReviewsTab: '',
    };
  },
  validations: {
    funnelResource: {
      swipes: {
        $each: {
          name: {
            required,
            minLength: minLength(3),
            maxLength: maxLength(80),
          },
          subjects: {
            $each: {
              required,
              minLength: minLength(1),
              maxLength: maxLength(300),
            },
          },
          $trackBy: 'id',
        },
      },
      social_media_posts: {
        facebook: {
          $each: {
            name: {
              required,
              minLength: minLength(3),
              maxLength: maxLength(80),
            },
            post: {
              required,
              minLength: minLength(2),
            },
            $trackBy: 'id',
          },
        },
        twitter: {
          $each: {
            name: {
              required,
              minLength: minLength(3),
              maxLength: maxLength(80),
            },
            tweet: {
              required,
              minLength: minLength(2),
            },
            $trackBy: 'id',
          },
        },
        linkedin: {
          $each: {
            name: {
              required,
              minLength: minLength(3),
              maxLength: maxLength(80),
            },
            post: {
              required,
              minLength: minLength(2),
            },
            $trackBy: 'id',
          },
        },
      },
      blog_reviews: {
        $each: {
          name: {
            required,
            minLength: minLength(3),
            maxLength: maxLength(80),
          },
          title: {
            required,
            minLength: minLength(3),
            maxLength: maxLength(80),
          },
          $trackBy: 'id',
        },
      },

      thankyou_page_ads: {
        $each: {
          name: {
            required,
            minLength: minLength(3),
            maxLength: maxLength(80),
          },
          $trackBy: 'id',
        },
      },

      logout_page_ads: {
        $each: {
          name: {
            required,
            minLength: minLength(3),
            maxLength: maxLength(80),
          },
          $trackBy: 'id',
        },
      },

      facebook_ads: {
        $each: {
          name: {
            required,
            minLength: minLength(3),
            maxLength: maxLength(80),
          },
          copy: {
            required,
            minLength: minLength(3),
          },
          interests: {},
          $trackBy: 'id',
        },
      },

      video_reviews: {
        $each: {
          name: {
            required,
            minLength: minLength(3),
            maxLength: maxLength(80),
          },
          description: {
            required,
            minLength: minLength(3),
          },
          video: {
            required,
            url,
            isValidVideoURL(value, object) {
              if (urlParser.parse(value) === undefined) {
                return false;
              }
              return true;
            },
          },
          $trackBy: 'id',
        },
      },

      signatures: {
        email: {
          $each: {
            name: {
              required,
              minLength: minLength(3),
              maxLength: maxLength(80),
            },
            post: {
              required,
              minLength: minLength(2),
            },
            $trackBy: 'id',
          },
        },
        forum: {
          $each: {
            name: {
              required,
              minLength: minLength(3),
              maxLength: maxLength(80),
            },
            post: {
              required,
              minLength: minLength(2),
            },
            $trackBy: 'id',
          },
        },
      },

      press_releases: {
        $each: {
          name: {
            required,
            minLength: minLength(3),
            maxLength: maxLength(80),
          },
          press_release: {
            required,
            minLength: minLength(3),
          },
          $trackBy: 'id',
        },
      },
      articles: {
        $each: {
          name: {
            required,
            minLength: minLength(3),
            maxLength: maxLength(80),
          },
          article: {
            required,
            minLength: minLength(3),
          },
          $trackBy: 'id',
        },
      },
    },
  },
  computed: {
    // Same endpoint the Quill imageUploader posted to before the rich-text
    // editor was replaced; `GsRichTextEditor` sends the identical multipart
    // `file` field and reads `response.data.url` back.
    uploadUrl() {
      return `${window.gdEnv.VUE_APP_API_URL}/${this.getDigitalAppTitle()}/images/upload/quill`;
    },
    assetTypeSummaries() {
      return summariseAssetTypes(this.funnelResource, this.assetsErrorTabs).map(
        (summary) => ({ ...summary, label: this.$t(summary.label) })
      );
    },
    currentTypeIndex() {
      const index = ASSET_TYPES.findIndex((type) => type.value === this.assetsTab);
      return index === -1 ? 0 : index;
    },
    currentType() {
      return ASSET_TYPES[this.currentTypeIndex];
    },
    currentTypeLabel() {
      return this.$t(this.currentType.labelKey);
    },
    currentTypeGuidance() {
      return this.currentType.guidance;
    },
    assetsErrorTabs() {
      let assetsErrorTabs = [];
      // Values on the right MUST be `ASSET_TYPES[].value` (see ./assetTypes.js) —
      // that is what the sidebar matches on. `press_releases` and `articles` both
      // live under the single `allArticles` asset type.
      const assetMappings = {
        swipes: 'swipes',
        blog_reviews: 'blogReviews',
        thankyou_page_ads: 'thankyouPageAds',
        logout_page_ads: 'logoutPageAds',
        facebook_ads: 'facebookAds',
        video_reviews: 'videoReviews',
        social_media_posts: 'socialMediaPosts',
        press_releases: 'allArticles',
        articles: 'allArticles',
        signatures: 'signatures'
      };

      Object.keys(assetMappings).filter((key) => {
        if (this.$v.funnelResource[key]?.$error
          && assetsErrorTabs.indexOf(assetMappings[key]) === -1) {
          assetsErrorTabs.push(assetMappings[key]);
        }
      });

      return assetsErrorTabs;
    },
    landingPages() {
      const landingPages = [];

      const hasFunnel = this.funnel && this.funnel.funnel_products_count > 0;
      const mainProduct = hasFunnel && this.funnel.main_product;

      if (mainProduct && mainProduct.affiliates_enabled && mainProduct.affiliate_landing_pages && mainProduct.affiliate_landing_pages.length > 0) {
        const allLandingPages = mainProduct.affiliate_landing_pages;

        for (const landingPage of allLandingPages) {
          const isAssignedToZero = landingPage.assigned_to.length == 1 && landingPage.assigned_to[0].id == 0;

          if (isAssignedToZero) {
            landingPages.push({
              id: landingPage.id,
              description: landingPage.description,
            });
          }
        }
      }

      return landingPages;
    },
  },
  watch: {
    // The asset types edit the promo area in place (`localFunnelResource` is
    // this same object), so a `v-model` on a name, a body or a toggle never
    // reaches a handler here. Watching the object itself is what catches all
    // of them - the entry points that do emit `updateFunnelResource` mark the
    // flag directly as well, so a whole-object replacement counts too.
    funnelResource: {
      deep: true,
      handler() {
        this.isDirty = true;
      },
    },
  },
  async created() {
    const user = Auth.authenticatedUser();
    this.$wait.start('assets');

    this.funnel = await Funnel.$find(this.$route.params.id);
    this.funnelResource = await FunnelResource.$find(
      this.$route.params.resource_id
    );

    [this.settingsFunnels, this.settingsResources] = await Promise.all([Funnel.$get(), this.funnel.promotional_areas().$get()]);

    this.swipesTab = this.updateTabIfEmpty(this.swipesTab, this.funnelResource, 'swipes', 'swipe');
    this.thankyouPageAdsTab = this.updateTabIfEmpty(this.thankyouPageAdsTab, this.funnelResource, 'thankyou_page_ads', 'thankyou_page_ad');
    this.emailSignaturesTab = this.updateTabIfEmpty(this.emailSignaturesTab, this.funnelResource.signatures, 'email', 'email_signature');
    this.forumSignaturesTab = this.updateTabIfEmpty(this.forumSignaturesTab, this.funnelResource.signatures, 'forum', 'forum_signature');
    this.facebookPostsTab = this.updateTabIfEmpty(this.facebookPostsTab, this.funnelResource.social_media_posts, 'facebook', 'fb_post');
    this.twitterTweetsTab = this.updateTabIfEmpty(this.twitterTweetsTab, this.funnelResource.social_media_posts, 'twitter', 'twitter_tweet');
    this.linkedinPostsTab = this.updateTabIfEmpty(this.linkedinPostsTab, this.funnelResource.social_media_posts, 'linkedin', 'linkedin_post');
    this.blogReviewsTab = this.updateTabIfEmpty(this.blogReviewsTab, this.funnelResource, 'blog_reviews', 'blog_review');
    this.facebookAdsTab = this.updateTabIfEmpty(this.facebookAdsTab, this.funnelResource, 'facebook_ads', 'facebook_ad');
    this.videoReviewsTab = this.updateTabIfEmpty(this.videoReviewsTab, this.funnelResource, 'video_reviews', 'video_review');
    this.logoutPageAdsTab = this.updateTabIfEmpty(this.logoutPageAdsTab, this.funnelResource, 'logout_page_ads', 'logout_page_ad');
    this.pressReleasesTab = this.updateTabIfEmpty(this.pressReleasesTab, this.funnelResource, 'press_releases', 'press_release');
    this.articlesTab = this.updateTabIfEmpty(this.articlesTab, this.funnelResource, 'articles', 'articles');

    this.snapshot();
    this.$wait.end('assets');
  },
  methods: {
    validatePortalSettings() {
      const editor = this.$refs && this.$refs.portalSettings;
      if (editor && !editor.validateAndApply()) {
        this.assetsTab = 'portalSettings';
        this.$gsToast.error('Fix the highlighted portal settings before saving');
        return false;
      }
      return true;
    },
    getDigitalAppTitle() {
      return this.whitelabelAppData('digital')?.title.toLowerCase() || 'groovedigital';
    },
    updateFunnelResource(funnelResource) {
      this.funnelResource = funnelResource;
      this.isDirty = true;
    },
    updateTabIfEmpty(tab, resource, key, tabName) {
      if (tab === '' && resource && resource[key] && resource[key].length > 0) {
        tab = `${tabName}_tab_${resource[key][0].id}`;
      }
      return tab;
    },
    toggleSwitchValue(type) {
      const propertyMap = {
        1: 'swipes_enabled',
        2: 'blog_reviews_enabled',
        3: 'banners_enabled',
        4: 'thankyou_page_ads_enabled',
        5: 'logout_page_ads_enabled',
        6: 'facebook_ads_enabled',
        7: 'social_media_posts_enabled',
        8: 'press_releases_enabled',
        9: 'video_reviews_enabled',
        10: 'signatures_enabled'
      };
      const propertyName = propertyMap[type];
      
      if (propertyName) {
        this.funnelResource[propertyName] = !this.funnelResource[propertyName];
        this.isDirty = true;
      }
    },
    // "Everything on screen is saved." Loading the promo area, and saving it,
    // both write to the watched object, so the notifications those writes have
    // already queued are cleared on the next tick as well.
    snapshot() {
      this.isDirty = false;
      this.$nextTick(() => {
        this.isDirty = false;
      });
    },
    async save() {
      if (!this.validatePortalSettings()) return;
      this.$root.$emit('trigger-validation');
      this.$v.$touch();
      const isValid = !this.$v.$invalid;

      if (!isValid) {
        this.$gsToast.error('Fix the highlighted sections before saving');
        return;
      }

      this.$loader.start('saveAssets');

      try {
        await this.funnelResource.save();
        this.snapshot();
        this.$gsToast.success('Promo tools saved');
      } catch (error) {
        this.$gsToast.error('Could not save your promo tools. Please try again.');
      } finally {
        this.$loader.end('saveAssets');
      }
    },
    async saveAssets() {
      if (!this.validatePortalSettings()) return;
      this.$root.$emit('trigger-validation');
      this.$v.$touch();
      const isValid = !this.$v.$invalid;

      if (isValid) {
        this.$loader.start('saveAssets');
        await this.funnelResource.save();
        this.$loader.end('saveAssets');
        this.snapshot();
        this.$router.push({
          name: 'funnel_resources',
          params: { id: this.funnel.id },
        });
      } else {
        // Save & exit used to fail silently: the page stayed put with no
        // reason given. Same sentence Save shows.
        this.$gsToast.error('Fix the highlighted sections before saving');
      }
    },
    sendToFunnelPromoAreas() {
      this.$router.push({
        name: 'funnel_resources',
        params: { id: this.funnel.id },
      });
    },
    handleAssetsTabChange(newTab) {
      this.assetsTab = newTab;
    },
    handleSwipesTabChange(newTab) {
      this.swipesTab = newTab;
    },
    handleBlogReviewsTabChange(newTab) {
      this.blogReviewsTab = newTab;
    },
    handleFacebookAdsTabChange(newTab) {
      this.facebookAdsTab = newTab;
    },
    handleVideoReviewsTabChange(newTab) {
      this.videoReviewsTab = newTab;
    },
    handleThankyouPageAdsTabChange(newTab) {
      this.thankyouPageAdsTab = newTab;
    },
    handleLogoutPageAdsTabChange(newTab) {
      this.logoutPageAdsTab = newTab;
    },
    handlePressReleasesTabChange(newTab) {
      this.pressReleasesTab = newTab;
    },
    handleArticlesTabChange(newTab) {
      this.articlesTab = newTab;
    },
    handleFacebookPostsTabChange(newTab) {
      this.facebookPostsTab = newTab;
    },
    handleTwitterTweetsTabChange(newTab) {
      this.twitterTweetsTab = newTab;
    },
    handleLinkedinPostsTabChange(newTab) {
      this.linkedinPostsTab = newTab;
    },
    handleEmailSignaturesTabChange(newTab) {
      this.emailSignaturesTab = newTab;
    },
    handleForumSignaturesTabChange(newTab) {
      this.forumSignaturesTab = newTab;
    },
  },
};
</script>

<style scoped>
.gs-asset-wizard__settings-heading { margin-bottom:20px; }
.gs-asset-wizard__settings-heading h1 { font-size:24px; font-weight:700; margin:0 0 8px; }
.gs-asset-wizard__settings-heading p { color:var(--gs-muted-foreground); font-size:14px; line-height:1.6; max-width:75ch; }

.gs-resources-assets {
  min-height: 100%;
  color: var(--gs-foreground);
  background: var(--gs-background);
}

.gs-asset-wizard__bar {
  position: sticky;
  top: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  gap: 20px;
  min-height: 64px;
  padding: 0 28px;
  background: var(--gs-surface);
  border-bottom: 1px solid var(--gs-border);
}

.gs-asset-wizard__back {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
  padding: 6px 10px 6px 6px;
  color: var(--gs-muted-foreground);
  font-size: 14px;
  font-weight: 600;
  background: transparent;
  border: 0;
  border-radius: var(--gs-radius-sm);
  cursor: pointer;
  transition: background-color 120ms ease, color 120ms ease;
}

.gs-asset-wizard__back:hover {
  color: var(--gs-foreground);
  background: var(--gs-muted);
}

.gs-asset-wizard__back:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--gs-ring);
}

.gs-asset-wizard__title {
  min-width: 0;
  flex: 1 1 auto;
}

.gs-asset-wizard__eyebrow {
  display: block;
  overflow: hidden;
  color: var(--gs-muted-foreground);
  font-size: 13px;
  font-weight: 600;
  line-height: 1.3;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.gs-asset-wizard__actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 0 0 auto;
}

.gs-asset-wizard__status {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  margin-right: 4px;
  color: var(--gs-muted-foreground);
  font-size: 13px;
  white-space: nowrap;
}

.gs-asset-wizard__status i {
  display: inline-block;
  width: 8px;
  height: 8px;
  background: var(--gs-success);
  border-radius: 999px;
}

.gs-asset-wizard__status.is-dirty i {
  background: var(--gs-danger);
}

.gs-asset-wizard__layout {
  display: grid;
  grid-template-columns: 260px minmax(0, 1fr);
  gap: 44px;
  max-width: 1260px;
  margin: 0 auto;
  padding: 32px 28px 56px;
}

.gs-asset-wizard__content {
  min-width: 0;
}

.gs-asset-wizard__panel {
  min-width: 0;
}

@media (max-width: 900px) {
  .gs-asset-wizard__layout {
    grid-template-columns: minmax(0, 1fr);
    gap: 24px;
    padding: 24px 18px 48px;
  }
}

@media (max-width: 720px) {
  .gs-asset-wizard__bar {
    flex-wrap: wrap;
    gap: 10px;
    padding: 12px 18px;
  }

  .gs-asset-wizard__title {
    order: 3;
    flex: 1 0 100%;
  }

  .gs-asset-wizard__actions {
    margin-left: auto;
  }
}
</style>

<style>
.swipesEditor {
  height: 32rem;
}

.gs-default-tabs-item:focus {
  outline: none !important;
  outline-style: none !important;
}

.embed-container {
  margin-bottom: 1.1em;
  margin-top: 1.5em;
  position: relative;
  padding-bottom: 56.25%;
  height: 0;
  overflow: hidden;
  max-width: 100%;
}
.embed-container iframe,
.embed-container object,
.embed-container embed {
  position: absolute;
  top: 0;
  left: 0;
  width: 96%;
  height: 100%;
  margin-left: 2%;
  margin-right: 2%;
}
</style>

<style scoped>
.custom-scrollbars {
  height: calc(100vh - 4rem)
}
</style>
